import React, { useState, useRef } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Box,
  Button,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";
import { Images } from "../../../utils/Images";
import { getImageSrc } from "../../../utils/imageMapping";

// Styled components for header section
const Container = styled(Box)(({ theme, isMobile }) => ({
  background: "#FFFFFF",
  // borderRadius: isMobile ? "0px" : "12px",
  // border: isMobile ? "none" : "1px solid #F1F1F1",
  // padding: isMobile ? "0px" : "30px 30px 7px 30px",
  width: "100%",
}));

const HeaderContainer = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: isMobile ? "10px" : "20px",
}));

const HeaderBorder = styled(Box)(({ theme, isMobile }) => ({
  height: isMobile ? "0.25px" : "0.51px",
  background: "#B9B9B8",
  marginBottom: isMobile ? "10px" : "20px",
}));

const SparkleContainer = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& img": {
    width: isMobile ? "20px" : "24px",
    height: isMobile ? "20px" : "24px",
  },
}));

const SectionTitle = styled(Typography)(({ theme, isMobile }) => ({
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: isMobile ? "18px" : "24px",
  lineHeight: isMobile ? "18px" : "20px",
  letterSpacing: "-2%",
  verticalAlign: "middle",
  color: "#1D1D1B",
}));

// Helper function to transform API story/project data to component format
const transformStoryData = (campaignData) => {
  if (!campaignData) return [];

  // Prioritize projectDescriptions as they match the story card structure better
  // Each project has image, title, and description which matches the UI
  if (campaignData.projects && Array.isArray(campaignData.projects) && campaignData.projects.length > 0) {
    return campaignData.projects.map((project, index) => ({
      id: project.id || index + 1,
      image: project.image || '',
      title: project.title || '',
      description: project.description || '',
      button: index === campaignData.projects.length - 1,
    }));
  }
  
  // Fallback to story data if projectDescriptions not available
  if (campaignData.story && campaignData.story.images && campaignData.story.images.length > 0) {
    // If story has images, create entries from story images
    // Images are already transformed to full URLs in campaignService
    return campaignData.story.images.map((img, index) => ({
      id: index + 1,
      image: typeof img === 'string' ? img : (img.url || img),
      title: index === 0 ? (campaignData.story.title || '') : '',
      description: index === 0 ? (campaignData.story.description || '') : '',
      button: index === campaignData.story.images.length - 1,
    }));
  }

  return [];
};

const MedicalList = ({ onStoryButtonClick, showPatientHeader = false, showFullContentByDefault = false, campaignData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showFullContent, setShowFullContent] = useState(showFullContentByDefault);
  const [videoStarted, setVideoStarted] = useState({});
  const videoRefs = useRef({});

  // Get story data from API or use empty array
  const medicalData = transformStoryData(campaignData);
  
  // Filter data based on showFullContent
  const displayData = showFullContent ? medicalData : medicalData.slice(0, 1);

  const handleReadMore = () => {
    setShowFullContent(true);
  };

  // Helper function to check if media is a video
  const isVideo = (mediaUrl) => {
    if (!mediaUrl) return false;
    
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.m4v'];
    const urlLower = mediaUrl.toLowerCase();
    return videoExtensions.some(ext => urlLower.includes(ext));
  };

  // Helper function to get media source
  const getMediaSrc = (mediaPath) => {
    // For full URLs, return as-is
    if (mediaPath && (mediaPath.startsWith('http://') || mediaPath.startsWith('https://'))) {
      return mediaPath;
    }
    // For local paths, use imageMapping utility
    return getImageSrc(mediaPath);
  };

  return (
    <Container isMobile={isMobile}>
      {/* Header with star icon - same as Projects */}
      <HeaderContainer isMobile={isMobile}>
        <SparkleContainer isMobile={isMobile}>
          <img src={Images.streamlineSharpStar} alt="Star" />
        </SparkleContainer>
        <SectionTitle isMobile={isMobile}>
          Story
        </SectionTitle>
      </HeaderContainer>
      <HeaderBorder isMobile={isMobile} />

      {/* Content */}
      <Box sx={{ 
        flexGrow: 1, 
        position: 'relative', 
        marginBottom: isMobile ? '10px' : (showFullContent || medicalData.length === 1) ? '30px' : '150px' 
      }}>
        {medicalData.length === 0 ? (
          <Typography
            sx={{
              fontSize: isMobile ? "12px" : "14px",
              color: "#686866",
              textAlign: "center",
              py: 4,
            }}
          >
            No story content available at the moment.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {displayData.map(({ id, image, title, description, button }) => (
            <Grid item xs={12} sm={6} md={3} key={id} sx={{ width: "100%" }}>
              <Card sx={{ boxShadow: 0, position: 'relative' }}>
                {isVideo(image) ? (
                  <Box sx={{ position: "relative", display: "block", borderRadius: "16px", overflow: "hidden" }}>
                    <Box
                      component="video"
                      ref={(el) => { if (el) videoRefs.current[id] = el; }}
                      src={getMediaSrc(image)}
                      controls
                      playsInline
                      onPlay={() => setVideoStarted((prev) => ({ ...prev, [id]: true }))}
                      sx={{
                        borderRadius: "16px",
                        width: "100%",
                        maxHeight: "450px",
                        height: isMobile ? "200px" : "auto",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    {!videoStarted[id] && (
                      <Box
                        onClick={() => { const v = videoRefs.current[id]; if (v) v.play(); }}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          background: "rgba(0,0,0,0.35)",
                          borderRadius: "16px",
                          "&:hover": { background: "rgba(0,0,0,0.45)" },
                        }}
                        aria-label="Play video"
                      >
                        <Box
                          sx={{
                            width: isMobile ? 56 : 72,
                            height: isMobile ? 56 : 72,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.95)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                          }}
                        >
                          <svg width={isMobile ? 24 : 32} height={isMobile ? 24 : 32} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 5v14l11-7L8 5z" fill="#1D1D1B" />
                          </svg>
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    image={getMediaSrc(image)}
                    alt={title || "medical"}
                    sx={{ 
                      borderRadius: "16px",
                      width: "100%",
                      maxHeight: "450px",
                      height: isMobile ? "200px" : "auto",
                      objectFit: "cover"
                    }}
                  />
                )}
                <CardContent sx={{ paddingLeft: "0px", paddingBottom: "0px" }}>
                  {title && (
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="div"
                      sx={{ fontSize: isMobile ? "12px" : "16px", fontWeight: "600", color: "#1D1D1B" }}
                    >
                      {title}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    color="#686866"
                    sx={{ 
                      fontSize: isMobile ? "12px" : "14px", 
                      fontWeight: "400",
                      "& *": {
                        color: "inherit",
                      },
                      // The story editor leaves empty paragraphs (<p></p>, <p><br></p>) and
                      // stacked <br>s between blocks; on mobile each rendered as a blank line,
                      // leaving big gaps before the next story image. Collapse them.
                      "& p:empty, & p:has(> br:only-child)": { display: "none" },
                      "& br + br": { display: "none" },
                      "& > :last-child": { marginBottom: 0 },
                    }}
                    dangerouslySetInnerHTML={{ __html: description || '' }}
                  />


                  {/* "Click Here To Know About The Story" — only render it when the
                      page actually gave us somewhere to go. Its onClick does nothing
                      but call onStoryButtonClick, so without a handler it was a dead
                      link; LifeDetailPageV2 doesn't pass one, which is why it showed
                      there and did nothing. The story page does pass one, so it keeps
                      the button. */}
                  {button && onStoryButtonClick && (showFullContent || medicalData.length === 1) && !showPatientHeader && (
                    <Box mt={2}>
                      <Button
                        onClick={() => onStoryButtonClick && onStoryButtonClick()}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          fontWeight: "800",
                          margin: "auto",
                          display: "block",
                          width: "90%",
                          color: "#0072BC",
                          maxWidth: "300px",
                          fontSize: isMobile ? "12px" : "14px",
                        }}
                      >
                        Click Here To Know About The Story
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            ))}
          </Grid>
        )}
        
        {/* Read More Button - hidden when showFullContentByDefault (e.g. life detail / story preview) */}
        {medicalData.length > 1 && !showFullContent && !showFullContentByDefault && (
          <Box 
            sx={{
              position: 'absolute',
              bottom: isMobile ? -35 : -75,
              left: 0,
              right: 0,
              height: isMobile ? '100px' : '164px',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              paddingBottom: '20px',
              background: 'linear-gradient(175.64deg, rgba(255, 255, 255, 0) -41.66%, #FFFFFF 66.03%)',
            }}
          >
            <Button
              onClick={handleReadMore}
              sx={{
                width: '147px',
                height: '48px',
                borderRadius: '8px',
                paddingTop: '14px',
                paddingBottom: '14px',
                gap: '10px',
                backgroundColor: '#1D1D1B',
                color: '#FFFFFF',
                fontFamily: 'Red Hat Display',
                fontWeight: 600,
                fontStyle: 'SemiBold',
                fontSize: '14px',
                lineHeight: '14px',
                letterSpacing: '0%',
                textTransform: 'capitalize',
                '&:hover': {
                  backgroundColor: '#2D2D2B',
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              Read More
              <Box 
                sx={{ 
                  width: '16px', 
                  height: '16px',
                  marginLeft: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }} 
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 16 16" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    d="M4 6L8 10L12 6" 
                    stroke="white" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default MedicalList;